import React, { useEffect, useState } from 'react';
import {
	fetchActiveAnnouncements,
	announcementTone,
} from '../../services/announcementService';
import { PageWrap, PageHead, Card } from './customerUi';

/* Announcements — a READ-ONLY feed, highest priority first then newest. No
   compose, no reply (two-way messaging is a separate parked layer). Reads the
   same active/non-expired set the AnnouncementBanner shows. `customer` role may
   read `announcements` (firestore.rules: read = isSignedIn()), so no rules
   change was needed.

   REVIEWED 2026-08-10 (portal review pass item 3). The wiring was already real
   — this page has always read live Firestore data. What it was NOT doing was
   honoring the fields staff actually set in ManageAnnouncements: `type`
   (Info / Success / Warning / Urgent), `priority` and `showExpiration` were all
   written by the composer and silently dropped here. An admin marking something
   Urgent at priority 10 got a plain card in date order, identical to a routine
   note. Ordering is fixed in the service so the Home hub inherits it; the type
   and expiry treatment is here.

   THE "NEW" DOT USED TO BE A LIE. It was pinned to index 0, so whichever
   announcement happened to be first wore a "new" marker forever — including a
   six-month-old one on a quiet feed. It is now time-based, and it is the only
   thing on the page that claims recency. */

const DAY = 24 * 60 * 60 * 1000;
const NEW_FOR_DAYS = 7;

const toDate = (ts) => (ts?.toDate ? ts.toDate() : null);

/* Recent things read better relative ("2 days ago"); older things need the
   actual date, and anything from a previous year needs the year or a family
   reading in January mistakes last March for this one. */
const fmtDate = (a) => {
	const d = toDate(a.createdAt);
	if (!d) return '';
	const age = Date.now() - d.getTime();
	if (age < DAY) return 'Today';
	if (age < 2 * DAY) return 'Yesterday';
	if (age < 7 * DAY) return `${Math.floor(age / DAY)} days ago`;
	const sameYear = d.getFullYear() === new Date().getFullYear();
	return d.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		...(sameYear ? {} : { year: 'numeric' }),
	});
};

const isNew = (a) => {
	const d = toDate(a.createdAt);
	return !!d && Date.now() - d.getTime() < NEW_FOR_DAYS * DAY;
};

/* Only shown when staff ticked showExpiration — it is their call whether a
   family should see the deadline, and the composer already asks. */
const fmtEnds = (a) => {
	if (!a.showExpiration) return '';
	const d = toDate(a.expiresAt);
	if (!d) return '';
	return `Ends ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const AnnouncementsFeed = () => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await fetchActiveAnnouncements();
				if (!cancelled) setItems(list);
			} catch (err) {
				console.error('Error loading announcements:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<PageWrap>
			<PageHead
				title='From the studio'
				subtitle='Studio news, most important first.'
			/>

			{loading && (
				<p style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
					Loading…
				</p>
			)}

			{!loading && loadError && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						We couldn’t load announcements just now. Please refresh, or{' '}
						<a href='/contact'>contact us</a> if it keeps happening.
					</p>
				</Card>
			)}

			{!loading && !loadError && items.length === 0 && (
				<Card style={{ padding: '20px 15px', textAlign: 'center' }}>
					<span
						style={{
							display: 'inline-flex',
							width: 40,
							height: 40,
							borderRadius: '50%',
							background: 'var(--psp-chip-track, rgba(0,0,0,.05))',
							color: 'var(--text-muted, #9aa2b1)',
							alignItems: 'center',
							justifyContent: 'center',
							marginBottom: 10,
						}}>
						<i className='fa-sharp fa-solid fa-feather' style={{ fontSize: 15 }} aria-hidden='true' />
					</span>
					<p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
						Nothing new right now — studio news lands here.
					</p>
				</Card>
			)}

			{!loading &&
				!loadError &&
				items.map((a) => {
					const tone = announcementTone(a.type);
					const ends = fmtEnds(a);
					return (
						<Card key={a.id} style={{ marginBottom: 11 }}>
							{/* A left edge in the type's colour, so Urgent is legible at a
							    glance without shouting a whole coloured card at a parent. */}
							<div style={{ display: 'flex' }}>
								<span
									aria-hidden='true'
									style={{ width: 4, flex: '0 0 auto', background: tone.token }}
								/>
								<div style={{ flex: 1, minWidth: 0, padding: '13px 15px 15px' }}>
									{/* Top-aligned, not centered: a two-line title with a
									    vertically-centred date reads as misaligned, and titles
									    wrap often at 375px. The small offsets sit the icon and
									    the date on the title's FIRST line. */}
									<div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
										<i
											className={`fa-sharp fa-solid ${tone.icon}`}
											style={{
												fontSize: 12,
												color: tone.token,
												flex: '0 0 auto',
												marginTop: 4,
											}}
											aria-hidden='true'
										/>
										<span
											style={{
												fontSize: '0.98rem',
												fontWeight: 'var(--fw-bold, 700)',
												color: 'var(--text-heading, #384971)',
												minWidth: 0,
											}}>
											{a.title || 'Studio announcement'}
										</span>
										{isNew(a) && (
											<span
												title='Posted in the last week'
												style={{
													width: 7,
													height: 7,
													borderRadius: '50%',
													background: 'var(--cyv-teal-600, #54ada7)',
													flex: '0 0 auto',
													marginTop: 7,
												}}
											/>
										)}
										<span
											style={{
												marginLeft: 'auto',
												fontSize: '0.72rem',
												color: 'var(--text-muted, #9aa2b1)',
												fontWeight: 'var(--fw-semibold, 600)',
												flex: '0 0 auto',
												marginTop: 3,
											}}>
											{fmtDate(a)}
										</span>
									</div>
									<div
										style={{
											fontSize: '0.86rem',
											color: 'var(--text-body, #4a5570)',
											lineHeight: 1.6,
											marginTop: 6,
											whiteSpace: 'pre-wrap',
											overflowWrap: 'anywhere',
										}}>
										{a.message}
									</div>
									{ends && (
										<div
											style={{
												marginTop: 8,
												fontSize: '0.74rem',
												fontWeight: 'var(--fw-semibold, 600)',
												color: 'var(--text-muted, #9aa2b1)',
											}}>
											<i
												className='fa-sharp fa-solid fa-hourglass-half'
												style={{ marginRight: 5 }}
												aria-hidden='true'
											/>
											{ends}
										</div>
									)}
								</div>
							</div>
						</Card>
					);
				})}
		</PageWrap>
	);
};

export default AnnouncementsFeed;
